import {useEffect, useState} from "react"
import {useParams} from "react-router-dom"
import {Skeleton} from "primereact/skeleton"

import SubHeader from "../SubHeader"
import {PreceptorTable} from "../preceptor/PreceptorTable"
import {useStableNavigate} from "../../utilities/StableNavigateContext"
import {wrappedGet} from "../../utilities/AxiosHelper"
import {getLocaleDate, capitalizePersonName} from "../../utilities/general"

const EducatorAssignPreceptorsPage = () => {
    const navigate = useStableNavigate()
    const {orientation_id} = useParams()
    const [isLoading, setIsLoading] = useState(true)
    const [orientation, setOrientation] = useState({})
    const [preceptors, setPreceptors] = useState([])

    useEffect(() => {
        loadData()
    }, [])

    useEffect(() => {
        if (orientation.name) document.title = `${orientation.name} - Assign Preceptors`
    }, [orientation.name])

    const loadData = async () => {
        try {
            const [orientationRes, preceptorsRes] = await Promise.all([
                wrappedGet(`/unit-orientation/${orientation_id}`),
                wrappedGet(`/preceptors`),
            ])
            setOrientation(orientationRes.data)
            setPreceptors(preceptorsRes.data?.results ?? preceptorsRes.data ?? [])
        } catch (e) {
            console.error("EducatorAssignPreceptorsPage loadData error:", e)
        } finally {
            setIsLoading(false)
        }
    }

    const handlePreceptorsChange = (results) => {
        setOrientation((prev) => ({...prev, preceptors: results}))
    }

    if (isLoading) {
        return (
            <div className="card p-8 mt-6">
                <Skeleton width="100%" height="10rem" />
            </div>
        )
    }

    return (
        <>
            <SubHeader title1="" title2="Assign Preceptors">
                <div className="d-flex flex-row flex-grow-1 justify-content-end align-items-center gap-3">
                    <button
                        className="btn btn-light-primary btn-sm ms-2"
                        onClick={() => navigate(`/educator-orientations/${orientation_id}`)}
                    >
                        {`Return to ${orientation.name}'s Details `}
                    </button>
                </div>
            </SubHeader>
            <div className="container-fluid d-flex flex-column">
                <div className="container px-4 px-sm-0">
                    <div className="d-flex flex-row h3 pt-5">{orientation.name}</div>
                    <div className="d-grid d-sm-flex flex-row flex-wrap fw-semibold fs-6 gap-3 gap-sm-2 pt-4 pb-6 py-sm-4">
                        <span className="d-flex align-items-start align-items-sm-center me-0 me-sm-5 mb-0 mb-sm-2">
                            <i className="fas fa-hospital me-2 text-primary"></i>
                            {orientation.unit_name}
                        </span>
                        <span className="d-flex align-items-start align-items-sm-center me-0 me-sm-5 mb-0 mb-sm-2">
                            <i className="fas fa-user-nurse text-primary fs-6 me-2"></i>
                            {orientation.position_name}
                        </span>
                        <span className="d-flex align-items-start align-items-sm-center me-0 me-sm-5 mb-0 mb-sm-2">
                            <i className="fas fa-clock-rotate-left me-2 text-primary"></i>
                            Prior Exp: {orientation.experience_level_name}
                        </span>
                        <span className="d-flex align-items-start align-items-sm-center me-0 me-sm-5 mb-0 mb-sm-2">
                            <i className="fas fa-graduation-cap me-2 text-primary"></i>
                            {orientation.nursing_program_completed},{" "}
                            {getLocaleDate(orientation.nurse_graduate_board_date)}
                        </span>
                        <span className="d-flex align-items-start align-items-sm-center me-0 me-sm-5 mb-0 mb-sm-2">
                            <i className="fas fa-flag me-2 text-primary"></i>
                            Hired: {getLocaleDate(orientation.hire_date)}
                        </span>
                        <span className="d-flex align-items-start align-items-sm-center me-0 me-sm-5 mb-0 mb-sm-2">
                            <i className="fas fa-calendar-check me-2 text-primary"></i>
                            Expected End Date: {getLocaleDate(orientation.expected_end_date)}
                        </span>
                        <span className="d-flex align-items-start align-items-sm-center me-0 me-sm-5 mb-0 mb-sm-2">
                            <i className="fas fa-calendar me-2 text-primary"></i>
                            Length: {orientation.standard_orientation_length_weeks}w
                        </span>
                        <span className="d-flex align-items-start align-items-sm-center me-0 me-sm-5 mb-0 mb-sm-2">
                            <i className="fas fa-user-group me-2 text-primary"></i>
                            Preceptors: {(orientation.preceptors ?? []).map((p) => capitalizePersonName(p.preceptor_name)).join(", ")}
                        </span>
                    </div>
                    <PreceptorTable
                        orientation={orientation}
                        preceptors={preceptors}
                        onPreceptorsChange={handlePreceptorsChange}
                    />
                </div>
            </div>
        </>
    )
}

export default EducatorAssignPreceptorsPage
